import React, { useCallback } from 'react';
import { connect } from '../../../../redux/store';
import {
  IconTypeahead,
  StaticTypeahead,
  C,
  openConfirm,
} from '@kineticdata/bundle-common';
import { I18n, Moment, slugify } from '@kineticdata/react';
import { DefaultActions } from './';
import { Markdown } from '../Markdown';
import {
  CONTENT_MANAGER_MODE,
  SHORT_DESCRIPTION_MAX_LENGTH,
  getStatusColor,
} from '../helpers';
import { List, Map } from 'immutable';
import classNames from 'classnames';
import moment from 'moment';

const enhance = connect((state, props) => {
  const isPublicMode = props.mode === CONTENT_MANAGER_MODE.PUBLIC;
  const isViewMode = props.mode !== CONTENT_MANAGER_MODE.EDIT;
  const id = props.activeItem && props.activeItem.id;
  const portfolios = !isPublicMode ? state.settingsPortfolio.dataList : List();

  return {
    isPublicMode,
    isViewMode,
    id,
    portfolios,
    portfoliosNameMap: portfolios.reduce(
      (map, p) => map.set(p.values[C.FIELD_PORTFOLIO_ID], p.values['Name']),
      Map(),
    ),
  };
});

const ServiceSettingsRenderer = enhance(
  ({
    mode,
    isPublicMode,
    isViewMode,
    content,
    values,
    activeItem,
    handleValuesChangeDirect,
    id,
    portfolios,
    portfoliosNameMap,
    ...props
  }) => {
    const handleShortDescriptionBlur = useCallback(
      e => {
        const el = e.target;
        const value = (el.value || '').trim();
        if (value.length > SHORT_DESCRIPTION_MAX_LENGTH) {
          const newValue = value.slice(0, SHORT_DESCRIPTION_MAX_LENGTH);
          openConfirm({
            title: 'Truncate Short Description?',
            body: (
              <div>
                <I18n>
                  Your short description is too long and will be truncated to{' '}
                  {SHORT_DESCRIPTION_MAX_LENGTH} characters:
                </I18n>{' '}
                <em>"{newValue}"</em>
              </div>
            ),
            actionName: 'Continue and Truncate',
            ok: () => {
              handleValuesChangeDirect(id, 'Short Description')(newValue);
            },
            cancel: () => {
              el.focus();
            },
          });
        }
      },
      [handleValuesChangeDirect, id],
    );

    return !!activeItem && mode !== CONTENT_MANAGER_MODE.PUBLIC ? (
      <>
        <h2>
          <I18n>{activeItem.title}</I18n>
        </h2>

        <div className="form form-unstyled">
          <div className="h6">
            <span
              className={`badge badge-${getStatusColor(
                values.get('Status'),
              )} badge-stylized text-muted`}
            >
              <I18n>{values.get('Status')}</I18n>
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="service-name" className="field-label">
              <I18n>Service Name</I18n>
            </label>
            <input
              type="text"
              id="service-name"
              value={values.get('Name')}
              onChange={props.handleValuesChange(id, 'Name')}
              disabled={isViewMode}
            />
          </div>

          <div className="form-group">
            <label htmlFor="service-desc" className="field-label">
              <I18n>Short Description</I18n>
            </label>
            <textarea
              type="text"
              id="service-desc"
              value={values.get('Short Description') || ''}
              onChange={props.handleValuesChange(id, 'Short Description')}
              onBlur={handleShortDescriptionBlur}
              disabled={isViewMode}
              rows="3"
            />
            <small className="d-flex p-1">
              <span className="flex-grow-1 text-muted">
                <I18n>
                  {`This short description will be shown in list views of services. Max ${SHORT_DESCRIPTION_MAX_LENGTH} characters.`}
                </I18n>
              </span>
              <span
                className={classNames('flex-shrink-0 ml-2', {
                  'text-danger':
                    (values.get('Short Description') || '').length >
                    SHORT_DESCRIPTION_MAX_LENGTH,
                })}
              >
                {SHORT_DESCRIPTION_MAX_LENGTH -
                  (values.get('Short Description') || '').length}{' '}
                / {SHORT_DESCRIPTION_MAX_LENGTH}
              </span>
            </small>
          </div>

          <div className="checkbox">
            <label className="field-label">
              <I18n>Type</I18n>
            </label>
            {ServiceSettingsRenderer.FIELDS['Type'].options.map(
              ({ label, value }, i) => (
                <label htmlFor={slugify(value)} key={slugify(value)}>
                  <input
                    name="type-radio-options"
                    type="radio"
                    id={slugify(value)}
                    checked={values.get('Type') === value}
                    onChange={props.handleValuesChangeChecked(
                      id,
                      'Type',
                      value,
                    )}
                    disabled={isViewMode}
                  />
                  <I18n>{label}</I18n>
                </label>
              ),
            )}
          </div>

          <div className="form-group">
            <label htmlFor="exp-date" className="field-label">
              <I18n>Expiration Date</I18n>
            </label>
            <input
              type="text"
              id="exp-date"
              value={
                values.get(C.FIELD_EXPIRATION_DATE)
                  ? moment(values.get(C.FIELD_EXPIRATION_DATE)).format(
                      Moment.formats.date,
                    )
                  : 'None'
              }
              disabled={true}
            />
            <small className="d-flex p-1">
              <I18n>
                The date when the content expires and will need to be updated.
                This date is set during the approval process.
              </I18n>
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="service-icon" className="field-label">
              <I18n>Icon</I18n>
            </label>
            <IconTypeahead
              value={values.get('Icon')}
              onChange={handleValuesChangeDirect(id, 'Icon')}
              minSearchLength={0}
              id="service-icon"
              disabled={isViewMode}
            />
          </div>

          <div className="form-group">
            <label htmlFor="service-portfolios" className="field-label">
              <I18n>Portfolio(s)</I18n>
            </label>
            <StaticTypeahead
              valueProp="id"
              labelProp="name"
              options={portfolios.toJS().map(p => ({
                id: p.values[C.FIELD_PORTFOLIO_ID],
                name: p.values['Name'],
              }))}
              value={values.get(C.FIELD_PORTFOLIO_ID, []).map(id => ({
                id,
                name: portfoliosNameMap.get(id) || `${id} (Invalid Portfolio)`,
              }))}
              onChange={vals =>
                handleValuesChangeDirect(id, C.FIELD_PORTFOLIO_ID)(
                  vals.toJS().map(kw => kw.id),
                )
              }
              search={{ fields: [{ name: 'name', operator: 'matches' }] }}
              components={{
                Input: isViewMode
                  ? (values.get(C.FIELD_PORTFOLIO_ID) || []).length > 0
                    ? () => null
                    : () => (
                        <div className="form-control-plaintext">
                          <em>None</em>
                        </div>
                      )
                  : undefined,
              }}
              multiple={true}
              minSearchLength={0}
              id="service-portfolios"
              disabled={isViewMode}
            />
            <small className="d-block">
              <I18n>What portfolio(s) does this service belong to?</I18n>
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="service-keywords" className="field-label">
              <I18n>Keywords</I18n>
            </label>
            <StaticTypeahead
              options={[
                ...values
                  .get('Name', '')
                  .split(' ')
                  .filter(Boolean),
              ].map(value => ({ value }))}
              value={(values.get('Keywords') || []).map(value => ({ value }))}
              onChange={vals =>
                handleValuesChangeDirect(id, 'Keywords')(
                  vals.toJS().map(kw => kw.value),
                )
              }
              search={{ fields: [{ name: 'value', operator: 'matches' }] }}
              allowNew={true}
              messages={{
                custom:
                  'No suggested keywords available. Type and press enter to add custom values.',
              }}
              components={{
                Input: isViewMode
                  ? (values.get('Keywords') || []).length > 0
                    ? () => null
                    : () => (
                        <div className="form-control-plaintext">
                          <em>None</em>
                        </div>
                      )
                  : undefined,
              }}
              multiple={true}
              minSearchLength={0}
              id="service-keywords"
              disabled={isViewMode}
            />
          </div>

          <div className="form-group">
            <label htmlFor="service-sort-priority" className="field-label">
              <I18n>Sort Priority</I18n>
            </label>
            <input
              type="text"
              id="service-sort-priority"
              value={values.get('Sort Priority')}
              onChange={props.handleValuesChange(id, 'Sort Priority')}
              disabled={isViewMode}
            />
            <small className="d-flex p-1">
              <I18n>
                Must be an integer. The sort priority defines the order in which
                services will be shown in lists (lower values show at the top).
                All services with the same sort priority will be sorted together
                alphabetically. Services without a sort priority will be shown
                last.
              </I18n>
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="grade" className="field-label">
              <I18n>Grade</I18n>
            </label>
            <select
              id="grade"
              value={values.get('Grade') || ''}
              onChange={props.handleValuesChange(id, 'Grade')}
              disabled={isViewMode}
            >
              <option />
              <option value="1">1 (New Service)</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
              <option value="6">6</option>
              <option value="7">7</option>
              <option value="8">8</option>
              <option value="9">9</option>
              <option value="10">10 (Complete)</option>
            </select>
            <small className="d-flex p-1">
              <I18n>
                The grade defining the readiness level of the content. Should be
                set manually during the revision process before submitting for
                approval.
              </I18n>
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="content">
              <I18n>Notes</I18n>
            </label>
            <Markdown
              initialValue={activeItem.value}
              onChange={props.handleContentChangeDirect(id, 'value')}
              minHeight="200px"
              disabled={isViewMode}
              imageDir={props.imageDir}
              autofocus={false}
            />
          </div>

          {!isViewMode && (
            <DefaultActions
              id={id}
              isDirty={activeItem.isDirty}
              handleResetSection={props.handleResetSection}
              actionsPending={props.actionsPending}
            />
          )}
        </div>
      </>
    ) : null;
  },
);

// ID of secton using this renderer
ServiceSettingsRenderer.ID = 'SERVICE_SETTINGS';
ServiceSettingsRenderer.NAME = 'Service Settings';

// Define fields managed by this section so we can know which fields to reset
// when the section is reset. Can store additional metadata for fields to use in
// the renderer above.
ServiceSettingsRenderer.FIELDS = {
  Name: {},
  'Short Description': {},
  Icon: {},
  Type: {
    options: [
      { label: 'Public', value: 'Public' },
      { label: 'Internal', value: 'Internal' },
    ],
  },
  Grade: {},
  Keywords: {},
  [C.FIELD_PORTFOLIO_ID]: {},
};

export default ServiceSettingsRenderer;
